import { Alert } from "antd";
import DocumentTitle from "react-document-title";
import { signInWithEmail } from "../../../data/firebase/auth";
import { useEffect, useState } from "react";
import { generatePath, Link, useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { loadUser } from "../../../data/redux/user";
import { RoutePaths } from "../../routes";
import LoginForm, { LoginModel } from "./LoginForm";
import { appName } from "../../../common";
import Title from "../../../views/Components/Title";
import BackButton from "../../Components/MobileHeader/BackButton";
import {
  analyticsLogEvent,
  analyticsReportError,
} from "../../../data/firebase/analytics";
import { selectIsSignedIn } from "@/data/redux/user/selectors";

const errorCodes = {
  "auth/invalid-email": "Invalid email",
  "auth/user-disabled": "This account is disabled",
  "auth/user-not-found": "Invalid email or password",
  "auth/wrong-password": "Invalid password",
  "auth/too-many-requests": "Too many login attempts",
};

const redirectRoute = RoutePaths.myDefaultWishlist;

interface ErrorType {
  code: keyof typeof errorCodes;
}

export default function Login() {
  const [formError, setFormError] = useState<string | undefined>(undefined);
  const [successMessage, setSuccessMessage] = useState<string | undefined>(
    undefined,
  );
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isSignedIn = useSelector(selectIsSignedIn);

  useEffect(() => {
    if (isSignedIn) {
      analyticsLogEvent("login_redirect");
      navigate(redirectRoute);
    }
  }, [isSignedIn]);

  const onFinish = async ({ email, password, remember }: LoginModel) => {
    try {
      setIsLoading(true);
      const user = await signInWithEmail(email, password, remember);
      setSuccessMessage("Welcome!");
      dispatch(
        loadUser({ id: user.uid, name: user.displayName || "Anonymous" }),
      );
      analyticsLogEvent("login_success");
      navigate(generatePath(RoutePaths.myDefaultWishlist));
    } catch (error) {
      const errorCode: keyof typeof errorCodes = (error as ErrorType).code;
      setFormError(
        (error as ErrorType).code in errorCodes
          ? errorCodes[errorCode]
          : "Unknown error",
      );
      analyticsReportError(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DocumentTitle title={`${appName} — sign in`}>
      <div className="flex min-h-screen flex-col justify-between">
        <div className="text-base fixed h-6 py-1">
          <BackButton />
        </div>
        <Title>Sign In</Title>
        <div
          className={
            "mx-auto flex max-w-sm flex-col content-center items-center justify-center p-5"
          }
        >
          <div className="wish-text">
            Don&apos;t have an account?{" "}
            <Link to={RoutePaths.registration}>Sign up</Link>
          </div>
          {successMessage ? (
            <Alert type="success" message={successMessage} />
          ) : (
            <LoginForm
              onFinish={onFinish}
              formError={formError}
              isLoading={isLoading}
            />
          )}
        </div>
        <div></div>
      </div>
    </DocumentTitle>
  );
}
